<template>
  <div
    class="cloud-enabled-container"
    data-testid="settings-cloud-tab-enabled-Paused-expired"
  >
    <div
      v-if="getMenuOptionsByStatus && userRolePermissions.authorize?.value?.settings.enabled.cloud"
      class="menu-container"
    >
      <ib-inline-menu
        v-model:value="menuSelection"
        :options="getMenuOptionsByStatus"
        data-testid="settings-cloud-tab--inline-menu"
        @change="menuSelectionChanged"
      ></ib-inline-menu>
    </div>
    <div class="main-container">
      <div class="row settings-content-box settings-title-box">
        <h2
          class="ibf-h2"
          data-testid="settings-cloud-tab--title"
        >
          {{ getFeature('statusText') }}
        </h2>
        <h6
          class="ibf-h6 app_text_grey"
          data-testid="settings-cloud-tab--description"
        >
          {{ getFeature('secondaryStatus') }}
        </h6>
      </div>
      <div
        class="row settings-content-box settings-icon-box"
        data-testid="settings-cloud-tab--img"
      >
        <img
          :src="getFeature('icon')"
          :alt="getFeature('iconAlt')"
          width="104"
        />
      </div>
      <div class="row settings-content-box cta-button-box">
        <IncrediButton
          :disabled="!userRolePermissions.authorize?.value?.settings.enabled.cloud"
          class="cta-button justify-center"
          design="primary-purple"
          size="small"
          :rounded="true"
          data-testid="settings-cloud-tab--cloud-settings-button"
          @click.prevent="emitEvent(getFeature('ctaEvent'))"
        >
          <div class="flex justify-center items-center">
            <i
              v-if="getFeature('ctaIconName')"
              :class="getFeature('ctaIconName')"
            />
            <span class="button-label">{{ getFeature('ctaButtonText') }}</span>
          </div>
        </IncrediButton>
      </div>
    </div>
    <div
      class="connect-sales-container"
      data-testid="settings-cloud-tab--connect-sales-button"
    >
      <a :href="salesMailUrl">
        <q-btn
          no-caps
          class="sales-button incredi-button-ibx primary-light"
          rounded
          flat
        >
          <q-icon
            class="icon sales-button-icon"
            name="mail_outline"
          ></q-icon>
          <span class="button-label">Connect with sales</span>
        </q-btn>
      </a>
    </div>
  </div>
</template>

<script>
import IbInlineMenu from '@/components/common/InlineMenu.vue';
import cloudPaused_IMG from '@/assets/images/settings/cloud/cloud-paused.png';
import cloudEnabled_IMG from '@/assets/images/settings/cloud/cloud-enabled.png';
import cloudExpired_IMG from '@/assets/images/settings/cloud/cloud-expired.png';
import { LICENSE_TYPES } from '@/const/licensePage';
import { defineComponent, inject } from 'vue';
import { useCommonDataQuery } from '@/graphql/codegen/generated/graphql';
import { CLOUD_STATUS } from '@/views/settings/settingsCloud/cloud.const';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useUserRolePermissions } from '@/services/UserRolePermissions/UserRolePermissions';
import { IncrediButton } from 'incredi-ui';
import { dateUtils } from '@/services/dateUtils';

export default defineComponent({
  name: 'CloudEnabled',
  components: { IbInlineMenu, IncrediButton },
  model: {
    events: ['cloudSettings', 'deactivateCloud', 'pauseCloud', 'resumeCloud', 'connectWithSales'],
  },
  props: {
    status: { type: String, required: true },
  },
  setup() {
    const userRolePermissions = useUserRolePermissions();
    const coordinatorsService = inject(IKCoordinatorsService);

    const GQL_useLicenseInfoQuery = useCommonDataQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
    return { GQL_useLicenseInfoQuery, userRolePermissions };
  },
  data() {
    return {
      salesMailUrl: process.env.VUE_APP_CLOUD_SALES_MAIL,
      statusState: {
        Enabled: {
          statusText: this.$t('settings.cloud.cloudEnabled.statusText'),
          secondaryStatus: this.$t('settings.cloud.cloudEnabled.secondaryStatus'),
          ctaButtonText: this.$t('settings.cloud.cloudEnabled.ctaButtonText'),
          ctaIconName: 'uil uil-setting',
          ctaEvent: 'cloudSettings',
          icon: cloudEnabled_IMG,
          iconAlt: this.$t('settings.cloud.cloudEnabled.statusText'),
        },
        Paused: {
          statusText: this.$t('settings.cloud.cloudPaused.statusText'),
          secondaryStatus: this.getCloudPausedString(),
          ctaButtonText: this.$t('settings.cloud.cloudPaused.ctaButtonText'),
          ctaIconName: false,
          ctaEvent: 'resumeCloud',
          icon: cloudPaused_IMG,
          iconAlt: this.$t('settings.cloud.cloudPaused.statusText'),
        },
        Expired: {
          statusText: this.$t('settings.cloud.cloudExpired.statusText'),
          secondaryStatus: this.$t('settings.cloud.cloudExpired.secondaryStatus'),
          ctaButtonText: this.$t('settings.cloud.cloudExpired.ctaButtonText'),
          ctaIconName: null,
          ctaEvent: 'connectWithSales',
          icon: cloudExpired_IMG,
          iconAlt: this.$t('settings.cloud.cloudExpired.statusText'),
        },
      },
      menuSelection: '',
    };
  },
  computed: {
    license() {
      return this.GQL_useLicenseInfoQuery?.result?.value?.ibManagerEntities?.license;
    },
    getMenuOptionsByStatus() {
      if (this.status === CLOUD_STATUS.Expired) return false;

      let menuOptions = [];
      if (this.status === CLOUD_STATUS.Enabled) menuOptions = [{ id: 'pauseCloud' }];

      if (this.status === CLOUD_STATUS.Paused) menuOptions = [{ id: 'cloudSettings', name: 'Settings' }];

      if (this.license?.type !== LICENSE_TYPES.Marketplace) {
        menuOptions.push({ id: 'deactivateCloud', name: 'Deactivate' });
      }
      return menuOptions;
    },
  },
  methods: {
    menuSelectionChanged(event) {
      this.$emit(event.id);
    },
    getFeature(name) {
      return this.statusState[this.status][name];
    },
    emitEvent(event, payload = {}) {
      this.$emit(event, payload);
    },
    getCloudPausedString() {
      const pauseDateUTC = localStorage.getItem('CloudPausedDate');
      if (pauseDateUTC) {
        return `${dateUtils.getFormattedDate(Number(pauseDateUTC))} at ${dateUtils.getFormattedTime(Number(pauseDateUTC))}`;
      }
      return this.$t('settings.cloud.cloudPaused.secondaryStatus');
    },
  },
});
</script>

<style lang="scss" scoped>
.cloud-enabled-container {
  padding: 30px;
  position: relative;
  height: 100%;

  & h2,
  & h6 {
    margin-bottom: 0;
    margin-top: 0;
    height: 30px;
    line-height: 30px;
    width: 100%;
    text-align: center;
  }

  & .menu-container {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  & .main-container {
    position: absolute;
    height: 280px;
    width: 440px;
    top: 50%;
    margin-top: -140px;
    left: 50%;
    margin-left: -220px;

    & .settings-content-box {
      width: 100%;
      display: flex;
      justify-content: center;
      margin-bottom: 50px;
    }
  }

  & .connect-sales-container {
    position: absolute;
    bottom: 30px;
    right: 30px;
    & .sales-button {
      & .sales-button-icon {
        font-size: 16px;
        margin-right: 10px;
      }
    }

    & a,
    & a:link,
    & a:visited,
    & a:hover,
    & a:active {
      text-decoration: none;
    }
  }
}
</style>
